/**
 * reconstructLoyaltyTimeline — pure helper for the Clients CRM page.
 *
 * Replays the relationship events (oldest first) starting from 30 (the
 * roster baseline). Returns an array of {day, score, label?, delta?}
 * points suitable for a recharts AreaChart.
 *
 * 03/06/2026 — Created for the Phase 2 Clients page refactor.
 *
 * @param {{score:number, events:Array}} clientData - one entry from `clientLoyalty`
 * @param {number} today - state.totalDays
 * @param {number} initial - starting score (default 30 per resetRoster)
 * @returns {Array<{day:number, score:number, label?:string, delta?:number}>}
 */
export function reconstructLoyaltyTimeline(clientData, today, initial = 30) {
  const events = Array.isArray(clientData?.events) ? clientData.events : [];
  // Events are stored newest-first; reverse for chronological replay.
  const chronological = [...events].reverse();

  const points = [{ day: 0, score: initial }];
  let score = initial;
  for (const e of chronological) {
    score = Math.max(0, Math.min(100, score + (e.delta || 0)));
    points.push({
      day: e.day,
      score,
      label: e.label,
      delta: e.delta,
      contractName: e.contractName,
    });
  }
  // Anchor "today" so the area extends to the right edge.
  if (today > (points[points.length - 1]?.day || 0)) {
    points.push({ day: today, score });
  }
  return points;
}

/**
 * computeClientActionPlan — generates a 1-3 bullet recommendation array
 * tailored to the client's current score, cooldown state, and recent
 * event history.
 */
export function computeClientActionPlan(client, data, tier) {
  const score = data?.score || 0;
  const events = data?.events || [];
  const recentNegatives = events.slice(0, 5).filter(e => (e.delta || 0) < 0).length;
  const recentPositives = events.slice(0, 5).filter(e => (e.delta || 0) > 0).length;
  const onCooldown = (data?.coolingUntilDay || 0) > 0;

  const out = [];
  if (tier?.id === 'mefiant') {
    out.push({
      tone: 'urgent',
      headline: 'Restaurer la confiance',
      detail: 'Acceptez 2-3 contrats sans incident. Évitez les pénalités de retard et privilégiez les livraisons à temps. La période de gel se lèvera après 30 jours.',
    });
  } else if (tier?.id === 'neutre') {
    out.push({
      tone: 'normal',
      headline: 'Construire la relation',
      detail: 'Capitalisez sur chaque livraison. Une suite de 3 livraisons réussies dynamise la fidélité de +6 pts. Visez un cycle propre avant tout up-sell.',
    });
  } else if (tier?.id === 'confiant') {
    out.push({
      tone: 'normal',
      headline: 'Maintenir la cadence',
      detail: 'Le client commence à vous faire confiance. Acceptez les contrats compatibles avec vos thèmes préférés et tenez vos engagements.',
    });
  } else if (tier?.id === 'fidele') {
    out.push({
      tone: 'positive',
      headline: 'Activer la prime fidélité',
      detail: 'Vous bénéficiez d\'une remise tarif de −2 % automatique. Proposez des contrats à plus forte marge — le client acceptera plus facilement.',
    });
  } else if (tier?.id === 'partenaire') {
    out.push({
      tone: 'positive',
      headline: 'Cultiver le statut partenaire',
      detail: 'Statut maximum atteint. Maintenez la qualité, et envisagez des contrats internationaux ou spécialisés à haute valeur ajoutée.',
    });
  }

  if (onCooldown) {
    out.push({
      tone: 'urgent',
      headline: 'Période de gel active',
      detail: `Ce client refuse temporairement de nouveaux contrats. Plus que ${Math.max(0, (data.coolingUntilDay || 0) - (data.lastCheckedDay || 0))} jours avant réouverture.`,
    });
  }

  if (recentNegatives >= 2) {
    out.push({
      tone: 'urgent',
      headline: 'Série d\'incidents récents',
      detail: `${recentNegatives} événements négatifs sur les 5 derniers. Diagnostiquez : ressources mal allouées (véhicule en panne, chauffeur indisponible) ou contrats inadaptés à vos capacités.`,
    });
  } else if (recentPositives >= 3) {
    out.push({
      tone: 'positive',
      headline: 'Dynamique vertueuse',
      detail: `${recentPositives} succès récents. Capitaliser : prendre 1 contrat plus exigeant que d'habitude pour passer un cap de tier.`,
    });
  }

  if (score >= 95) {
    out.push({
      tone: 'positive',
      headline: 'Saturation du tier Partenaire',
      detail: 'Score quasi maximal. Pensez à recommander ce client à vos commerciaux pour générer du bouche-à-oreille (boost notoriété).',
    });
  }

  return out.slice(0, 3);
}

/**
 * computeClientKPIs — derives 3 quick metrics for the detail panel.
 */
export function computeClientKPIs(client, data, activeContracts = []) {
  const events = data?.events || [];
  const contractsActiveCount = activeContracts.filter(c => c.clientId === client.id).length;
  const incidents12Mo = events.filter(e => (e.delta || 0) < 0).length;
  // CA réalisé — approximation via somme des deltas positifs (proxy fidélité).
  // Pour le vrai CA, il faudrait croiser monthlyHistory — laissé pour Phase 3.
  const positiveEventsCount = events.filter(e => (e.delta || 0) > 0).length;
  return {
    contractsActive: contractsActiveCount,
    eventsTotal: events.length,
    positives: positiveEventsCount,
    incidents: incidents12Mo,
  };
}
